
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { BlogPost } from "@/types/blogTypes";
import { fetchBlogPosts as fetchBlogPostsD1 } from "@/services/d1Service";
import {
  getBlogPostsQueryKey,
  readPreloadedBlogPosts,
} from "@/utils/ssr/preloadedState";

// Re-export for backward compatibility
export type { BlogPost } from "@/types/blogTypes";
export { getBlogPostBySlug, saveBlogPost, deleteBlogPost } from "@/services/blogPostService";

interface UseBlogPostsOptions {
  limit?: number;
  published?: boolean | "all";
  orderBy?: string;
  random?: boolean;
}

export function useBlogPosts(options: UseBlogPostsOptions = {}) {
  const initialPosts = readPreloadedBlogPosts(options);
  const initialQueryKey = getBlogPostsQueryKey(options);
  const [posts, setPosts] = useState<BlogPost[]>(() => initialPosts ?? []);
  const [isLoading, setIsLoading] = useState(() => initialPosts === null);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchPosts = async (retryCount = 0) => {
      try {
        setIsLoading(true);
        setError(null);

        if (retryCount > 0) {
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }

        const data = await fetchBlogPostsD1({
          published: options.published,
          limit: options.limit,
        });

        const finalData = options.random
          ? [...data].sort(() => Math.random() - 0.5).slice(0, options.limit || 3)
          : data;

        setPosts(finalData);
      } catch (err: any) {
        setError(err);

        if (retryCount < 2 && (err.message.includes('network') || err.message.includes('fetch') || err.message.includes('timeout'))) {
          return fetchPosts(retryCount + 1);
        }

        const errorMessage = err.message || "Failed to load blog posts. Please try again.";
        toast({
          title: "Error fetching blog posts",
          description: `${errorMessage} ${retryCount > 0 ? `(After ${retryCount} retries)` : ''}`,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, [options.limit, options.published, options.orderBy, options.random, toast]);

  return { posts, isLoading, error };
}
